import React, { useState } from 'react'

import { getErrorMessage } from 'utils'

import type { Owner } from 'store/owners/types'
import type { BulkDeleteOwnersResponse } from 'store/owners/types'

import { useSnackbar } from 'contexts'
import { useBulkDeleteOwners } from 'hooks/api'

import { useTheme, Stack, Typography, Chip, Avatar } from '@mui/material'

import { Modal } from '@repo/ui'
import { Button } from '@repo/ui'
import { Alert } from '@repo/ui'

interface DeleteOwnersModalProps {
  owners: Owner[]
  open: boolean
  onClose: () => void
  onDeleted?: (ownerIds: string[]) => void
}

export const DeleteOwnersModal = (props: DeleteOwnersModalProps) => {
  const { owners, open, onClose, onDeleted } = props
  const theme = useTheme()

  const { open: notification } = useSnackbar()
  const { bulkDeleteOwners, isLoading } = useBulkDeleteOwners()

  const [error, setError] = useState<string | null>(null)

  const handleDeleteOwners = async () => {
    setError(null)

    try {
      const ownerIds = owners.map(owner => owner.id)
      const result = await bulkDeleteOwners(ownerIds)

      if (result.success && result.data) {
        const { summary, results } = result.data

        // Handle notifications based on results
        if (summary.failed === 0) {
          if (summary.deleted > 0) {
          }
          if (summary.alreadyDeleted > 0) {
            notification({
              type: 'info',
              title: `${summary.alreadyDeleted} ${summary.alreadyDeleted === 1 ? 'owner was' : 'owners were'} already deleted`,
            })
          }
        } else {
          // Mixed results - show summary
          const deletedCount = summary.deleted + summary.alreadyDeleted
          const message = deletedCount > 0
            ? `${deletedCount} deleted, ${summary.failed} failed`
            : `${summary.failed} ${summary.failed === 1 ? 'owner' : 'owners'} failed to delete`

          notification({
            type: summary.deleted > 0 ? 'warning' : 'error',
            title: message,
            action: 'View details',
            // TODO: Show detailed results modal
          })
        }

        // Call success callback with successfully processed IDs
        const processedIds = results
          .filter(r => r.status === 'deleted' || r.status === 'already_deleted')
          .map(r => r.id)

        if (onDeleted && processedIds.length > 0) {
          onDeleted(processedIds)
        }

        handleClose()
      } else {
        const errorMessage = getErrorMessage(result.error || 'Failed to delete owners')
        setError(errorMessage)
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      setError(errorMessage)
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }


  return (
    <Modal
      title={`Delete ${owners.length} ${owners.length === 1 ? 'owner' : 'owners'}`}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleDeleteOwners}
            disabled={isLoading}
          >
            Delete
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete the following {owners.length === 1 ? 'owner' : 'owners'}?
        </Typography>

        {error && <Alert severity="error" description={error} />}

        <Stack spacing={2}>
          <Stack
            spacing={1}
            sx={{
              maxHeight: '300px',
              overflowY: 'auto',
              pr: 1,
            }}
          >
            {owners.map((owner) => (
              <Stack
                key={owner.id}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  p: 3,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.common.white,
                }}
              >
                <Avatar
                  src={owner.avatar || undefined}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: theme.palette.primary.main,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {owner.name.charAt(0).toUpperCase()}
                </Avatar>
                <Stack spacing={0}>
                  <Typography variant="body2" fontWeight="500" color="text.primary">
                    {owner.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {owner.email}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  )
}
